const HTTP_FORBIDDEN = 403;
const HTTP_GONE = 410;
const HTTP_INTERNAL_SERVER_ERROR = 500;

export const SHARED_DOWNLOAD_UNAVAILABLE_CODE = 'SHARED_DOWNLOAD_UNAVAILABLE';
export const SHARED_DOWNLOAD_UNAVAILABLE_MESSAGE = 'This share is no longer available.';

type SharedDownloadError = {
    status: number;
    code?: string;
    data: {
        message: string;
        [key: string]: unknown;
    };
};

export function createSharedDownloadUnavailableError(status: number = HTTP_FORBIDDEN): SharedDownloadError {
    return {
        status,
        code: SHARED_DOWNLOAD_UNAVAILABLE_CODE,
        data: {
            message: SHARED_DOWNLOAD_UNAVAILABLE_MESSAGE,
        },
    };
}

export function isHtmlResponseContentType(contentType?: string): boolean {
    return contentType?.split(';', 1)[0].trim().toLowerCase() === 'text/html';
}

export function isSharedDownloadUnavailableError(error: unknown): boolean {
    if (!error || typeof error !== 'object') {
        return false;
    }

    const candidate = error as Partial<SharedDownloadError>;

    return (
        candidate.code === SHARED_DOWNLOAD_UNAVAILABLE_CODE ||
        candidate.status === HTTP_FORBIDDEN ||
        candidate.status === HTTP_GONE
    );
}

export async function normalizeSharedDownloadRequestError(error: any): Promise<SharedDownloadError> {
    const status = typeof error?.response?.status === 'number' ? error.response.status : HTTP_INTERNAL_SERVER_ERROR;
    const responseData = await parseResponseData(error?.response?.data);
    const message =
        typeof responseData?.message === 'string'
            ? responseData.message
            : error?.message || 'Shared download request failed.';
    const normalized = {
        status,
        data: {
            ...(responseData && typeof responseData === 'object' ? responseData : {}),
            message,
        },
    };

    if (status === HTTP_FORBIDDEN || status === HTTP_GONE) {
        return {
            ...normalized,
            code: SHARED_DOWNLOAD_UNAVAILABLE_CODE,
        };
    }

    return normalized;
}

async function parseResponseData(data: unknown): Promise<Record<string, unknown> | null> {
    if (typeof Blob !== 'undefined' && data instanceof Blob) {
        return parseJson(await readBlobText(data));
    }

    if (typeof data === 'string') {
        return parseJson(data);
    }

    return data && typeof data === 'object' ? (data as Record<string, unknown>) : null;
}

function readBlobText(blob: Blob): Promise<string> {
    if (typeof blob.text === 'function') {
        return blob.text();
    }

    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
        reader.onerror = () => reject(reader.error);
        reader.readAsText(blob);
    });
}

function parseJson(value: string): Record<string, unknown> | null {
    try {
        const parsed = JSON.parse(value);

        return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
    } catch {
        return null;
    }
}
